import React, { memo, useEffect, useState } from "react";
import {
    Typography,
    Stack,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Box,
    IconButton,
    CircularProgress,
    Grid
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import configServ from "../../../services/config";
import AddItemVideo from "./AddItemVideo";
import { VideoCall } from "@mui/icons-material";
import { cellStyle, evenRowStyle, headerCellStyle, oddRowStyle, rowStyle } from "../../../utility/CustomStyle";

// FUNCTION
const ItemVideoList = ({ itemData, pagination = true }) => {
    //#region code
    const rowsPerPageOptions = [5, 10, 50];
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [videoList, setVideoList] = useState([]);
    const [editData, setEditData] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [openVideo, setOpenVideo] = useState(false);
    const [isVideoChanged, setIsVideoChanged] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchItemVideo = async () => {
        try {
            setIsLoading(true);
            const result = await configServ.getProductVideos({ "product_id": itemData.id });
            if (result.status === 200) {
                setVideoList(result.data);
            }
            else {
                setVideoList([]);
            }
        }
        catch (err) {
            console.log(err);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchItemVideo();
    }, [isVideoChanged]);

    const headers = ['VIDEO', 'TITLE', 'DESCRIPTION', 'STATUS', 'ACTION'];

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedData = pagination
        ? videoList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : videoList;

    const handleIframe = (params) => {
        try {
            if (params !== '') {
                // Replace 'youtu.be' with 'www.youtube.com/embed' in the URL
                const updatedParams = params.replace("youtu.be", "www.youtube.com/embed");
                console.log("Updated URL:", updatedParams);
                return updatedParams; // Return the updated URL if needed
            }
            else {
                return '';
            }
        } catch (e) {
            console.log("Error:", e);
        }
    };

    const handleEdit = (data) => {
        setEditData(data);
        setIsEdit(true);
        setOpenVideo(true);
    }

    const handleVideoOpen = () => {
        setOpenVideo(true);
    }

    const handleVideoClose = () => {
        setIsVideoChanged(!isVideoChanged);
        handleReset();
    }

    const handleReset = () => {
        try {
            setOpenVideo(false);
            setEditData(null);
            setIsEdit(false);
        }
        catch (e) {
            console.log(e);
        }
    }
    //#endregion

    return (
        <>
            <AddItemVideo itemData={itemData} handleVideoClose={handleVideoClose} openVideo={openVideo} editData={editData} isEdit={isEdit} />

            <Grid container item alignItems={'center'} justifyContent={'flex-start'}>
                <IconButton onClick={handleVideoOpen} sx={{ fontSize: "36px", p: 0 }} title="Add Video">
                    <VideoCall color="warning" fontSize="36px" />
                </IconButton>
            </Grid>

            <TableContainer component={Paper} sx={{ width: '100%' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>S.No.</TableCell>
                            {headers.map((header, index) => (
                                <TableCell key={index} sx={headerCellStyle}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {
                            isLoading ?
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <CircularProgress />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                :
                                (
                                    paginatedData.length > 0 ?
                                        paginatedData.map((row, rowIndex) => (
                                            <TableRow key={rowIndex}
                                                sx={{
                                                    ...rowStyle,
                                                    ...(rowIndex % 2 === 0 ? evenRowStyle : oddRowStyle),
                                                }}
                                            >
                                                <TableCell sx={cellStyle}>{page * rowsPerPage + rowIndex + 1}</TableCell>
                                                <TableCell sx={cellStyle}>
                                                    <iframe width={200} height={100} src={handleIframe(row.video_url || '')}></iframe>
                                                </TableCell>
                                                <TableCell sx={cellStyle}>{row.title}</TableCell>
                                                <TableCell sx={cellStyle}>{row.description}</TableCell>
                                                <TableCell sx={cellStyle}>{Boolean(row.status) === true ? <CheckCircleIcon color='success' /> : <CancelIcon color='error' />}</TableCell>
                                                <TableCell sx={cellStyle}>
                                                    <Box sx={{ borderRadius: 10, width: 'fit-content', transition: '100ms', cursor: 'pointer', padding: 0.5, ":hover": { backgroundColor: '#99999955' } }}>
                                                        <Tooltip title='Edit' arrow>
                                                            <IconButton
                                                                onClick={() => { handleVideoOpen(); handleEdit(row) }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                        :
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                                                <Box display="flex" alignItems="center" justifyContent="center">
                                                    <Typography variant="body1" color={'red'} fontWeight={700}>No video found.</Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                )
                        }
                    </TableBody>
                </Table>
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={videoList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </TableContainer>
        </>
    )
}
export default memo(ItemVideoList);